import type {
  ShopAccess, ShopFileStatus, ShopOptionValues, ShopOrder, ShopOrderStatus, ShopServiceType, ShopSettings,
} from '../types/shop';

const API_BASE = '/api';

// Prevent concurrent refresh attempts
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * An API failure with its status and body intact.
 *
 * Callers that need to tell one failure from another — "the slot went" (409) vs
 * "you're short on credits" (402) — can't do it from a message string, and
 * guessing wrong means recovering the wrong way. Extends Error, so existing
 * `e instanceof Error ? e.message : …` handling keeps working untouched.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly data: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string>),
  };

  // Only set Content-Type for non-FormData bodies
  if (!(init?.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  if (res.status === 401) {
    // Try refresh once (deduplicated across concurrent requests)
    if (!refreshPromise) {
      refreshPromise = tryRefresh().finally(() => { refreshPromise = null; });
    }
    const refreshed = await refreshPromise;

    if (refreshed) {
      // Retry original request with new cookie
      const retryRes = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });
      if (retryRes.ok) return retryRes.json();
      if (retryRes.status === 401) {
        window.location.href = '/auth';
        throw new Error('Session expired');
      }
      const retryBody = await retryRes.json().catch(() => ({})) as { error?: string };
      throw new ApiError(retryBody.error ?? `API ${retryRes.status}`, retryRes.status, retryBody);
    }

    window.location.href = '/auth';
    throw new Error('Session expired');
  }

  if (res.status === 403) {
    const body = await res.json().catch(() => ({})) as { error?: string; portal?: string };
    if (body.error === '2FA_SETUP_REQUIRED') {
      window.dispatchEvent(new CustomEvent('fy:2fa-required', { detail: { portal: body.portal } }));
      throw new Error('2FA_SETUP_REQUIRED');
    }
    throw new ApiError(body.error ?? `API ${res.status}`, res.status, body);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new ApiError(body.error ?? `API ${res.status}`, res.status, body);
  }
  return res.json();
}

// ── Auth ──
export const authApi = {
  login: async (email: string, password: string, portalMode?: 'user' | 'admin') => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, portalMode: portalMode ?? 'user' }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? 'Login failed');
    }
    return res.json() as Promise<{
      user?: { id: string; firstName: string; username: string; roles: string[]; email: string };
      requires2FA?: boolean;
      requires2FASetup?: boolean;
      challengeToken?: string;
      userId?: string;
      portalMode?: 'user' | 'admin';
    }>;
  },
  verify2FA: async (challengeToken: string, code: string) => {
    const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ challengeToken, code }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? 'Verification failed');
    }
    return res.json() as Promise<{ user: { id: string; firstName: string; username: string; roles: string[]; email: string }; portalMode?: 'user' | 'admin' }>;
  },
  verify2FARecovery: async (challengeToken: string, recoveryCode: string) => {
    const res = await fetch(`${API_BASE}/auth/2fa/recovery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ challengeToken, recoveryCode }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? 'Recovery failed');
    }
    return res.json() as Promise<{
      user: { id: string; firstName: string; username: string; roles: string[]; email: string };
      portalMode?: 'user' | 'admin';
      remainingRecoveryCodes: number;
    }>;
  },
  register: (body: { chosenUsername: string; email: string; password: string; firstName: string; grade: string; emergencyPhone?: string; notes?: string; accountFor: 'self' | 'other'; turnstileToken: string; website?: string; formElapsedMs?: number; agreementIds?: string[] }) =>
    request<{ message: string; requiresVerification: boolean }>(
      '/auth/register',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // Public: the Portal Member Agreement shown as a read-and-sign gate on signup
  // (null when none is configured).
  registrationAgreement: () =>
    request<{ data: { id: string; title: string; body: string } | null }>(
      '/auth/registration-agreement',
    ).then((r) => r.data),
  logout: () => {
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  },
  forgotPassword: (email: string) =>
    request<{ message: string }>(
      '/auth/forgot-password',
      { method: 'POST', body: JSON.stringify({ email }) },
    ),
  resetPassword: (token: string, password: string) =>
    request<{ message: string }>(
      '/auth/reset-password',
      { method: 'POST', body: JSON.stringify({ token, password }) },
    ),
  resendVerification: () =>
    request<{ message: string }>(
      '/auth/resend-verification',
      { method: 'POST' },
    ),
  verifyPassword: async (password: string): Promise<{ valid: boolean }> => {
    const res = await fetch(`${API_BASE}/auth/verify-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ password }),
    });
    if (res.status === 401) return { valid: false };
    if (!res.ok) throw new Error('Verification failed');
    return { valid: true };
  },
};

// ── Current User ──
// ── Users ──
export const api = {
  me: {
    get: () => request<{ data: unknown }>('/me').then((r) => r.data),
    update: (body: unknown) => request('/me', { method: 'PATCH', body: JSON.stringify(body) }),
    twoFactor: {
      status: () => request<{ enabled: boolean; recoveryCodesRemaining: number }>('/me/2fa/status'),
      setup: () => request<{ secret: string; uri: string }>('/me/2fa/setup', { method: 'POST' }),
      confirm: (code: string) => request<{ recoveryCodes: string[] }>('/me/2fa/confirm', { method: 'POST', body: JSON.stringify({ code }) }),
      sendDisableCode: () => request<{ message: string; email: string }>('/me/2fa/send-disable-code', { method: 'POST' }),
      disable: (emailCode: string) => request<{ message: string }>('/me/2fa/disable', { method: 'POST', body: JSON.stringify({ emailCode }) }),
      regenerateCodes: (code: string) => request<{ recoveryCodes: string[] }>('/me/2fa/regenerate-codes', { method: 'POST', body: JSON.stringify({ code }) }),
    },
  },

  users: {
    // The /users endpoint is paginated (default 25, max 100 per page). The admin
    // portal's global `users` state needs every user so name lookups (volunteer
    // credits, timeslots, etc.) resolve instead of falling back to "Unknown",
    // so page through all results here.
    list: async () => {
      const first = await request<{ data: unknown[]; pagination: { totalPages: number } }>('/users?limit=100');
      const totalPages = first.pagination?.totalPages ?? 1;
      if (totalPages <= 1) return first.data;
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          request<{ data: unknown[] }>(`/users?limit=100&page=${i + 2}`).then((r) => r.data),
        ),
      );
      return [...first.data, ...rest.flat()];
    },
    get: (id: string) => request<{ data: unknown }>(`/users/${id}`).then((r) => r.data),
    onboarding: (id: string) =>
      request<{ data: import('../components/pos/types').PosOnboarding }>(`/users/${id}/onboarding`).then((r) => r.data),
    agreements: (id: string) =>
      request<{ data: import('../components/pos/types').PosAgreement[] }>(`/users/${id}/agreements`).then((r) => r.data),
    signAgreements: (id: string, body: { agreementIds: string[]; grantParentalConsent?: boolean; parentName?: string | null; parentEmail?: string | null }) =>
      request<{ data: { signed: number; consentGranted: boolean; activated: boolean } }>(`/users/${id}/sign-agreements`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    verifyEmail: (id: string) =>
      request<{ data: { alreadyVerified: boolean; activated: boolean } }>(`/users/${id}/verify-email`, { method: 'POST' }).then((r) => r.data),
    create: (body: unknown) => request('/users', { method: 'POST', body: JSON.stringify(body) }),
    bulkCreate: (body: {
      schoolOrGroup?: string | null;
      usernameFormat?: import('./username').UsernameFormat;
      sendWelcomeEmails?: boolean;
      sendConsentEmails?: boolean;
      consentGrantedBy?: string | null;
      summaryRecipients?: string[];
      users: Array<{
        firstName: string; lastName?: string | null; grade: string; email: string;
        usernameBase?: string; notes?: string | null; emergencyPhone?: string | null; pin?: number | null;
      }>;
    }) =>
      request<{ data: import('../types/user').BulkCreateResult }>('/users/bulk', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    update: (id: string, body: unknown) => request(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    updateGroup: (id: string, schoolOrGroup: string | null) =>
      request(`/users/${id}/group`, { method: 'PATCH', body: JSON.stringify({ schoolOrGroup }) }),
    delete: (id: string, reason?: string) => request(`/users/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason ?? '' }) }),
    revokeSignature: (userId: string, agreementId: string, reason: string) =>
      request(`/users/${userId}/revoke-signature`, { method: 'POST', body: JSON.stringify({ agreementId, reason }) }),
    reset2FA: (userId: string) =>
      request<{ message: string }>(`/users/${userId}/reset-2fa`, { method: 'POST' }),
    resendParentConsent: (userId: string) =>
      request<{ message: string }>(`/users/${userId}/resend-parent-consent`, { method: 'POST' }),
    sendFollowUp: (userId: string) =>
      request<{ sent: boolean; kind: string; message: string }>(`/users/${userId}/send-follow-up`, { method: 'POST' }),
  },

  agreements: {
    list: () => request<{ data: unknown[] }>('/agreements').then((r) => r.data),
    get: (id: string) => request<{ data: unknown }>(`/agreements/${id}`).then((r) => r.data),
    create: (body: unknown) => request('/agreements', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: unknown) => request(`/agreements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request(`/agreements/${id}`, { method: 'DELETE' }),
    revokeSignatures: (id: string) => request(`/agreements/${id}/revoke-signatures`, { method: 'POST' }),
    restore: (id: string) => request(`/agreements/${id}/restore`, { method: 'POST' }),
  },

  subscriptions: {
    list: () => request<{ data: unknown[] }>('/subscriptions').then((r) => r.data),
    tiers: () => request<{ data: unknown[] }>('/subscriptions/tiers').then((r) => r.data),
    perks: () => request<{ data: unknown[] }>('/subscriptions/perks').then((r) => r.data),
    get: (id: string) => request<{ data: unknown }>(`/subscriptions/${id}`).then((r) => r.data),
    create: (body: unknown) => request('/subscriptions', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: unknown) => request(`/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    allTransactions: () => request<{ data: unknown[] }>('/subscriptions/transactions').then((r) => r.data),
    transactions: (id: string) => request<{ data: unknown[] }>(`/subscriptions/${id}/transactions`).then((r) => r.data),
    userTransactions: (userId: string) => request<{ data: unknown[] }>(`/subscriptions/user/${userId}/transactions`).then((r) => r.data),
    userSubscriptions: (userId: string) => request<{ data: unknown[] }>(`/subscriptions/user/${userId}`).then((r) => r.data),
    createTier: (body: unknown) => request<{ data: unknown }>('/subscriptions/tiers', { method: 'POST', body: JSON.stringify(body) }),
    updateTier: (id: string, body: unknown) => request<{ data: unknown }>(`/subscriptions/tiers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteTier: (id: string) => request(`/subscriptions/tiers/${id}`, { method: 'DELETE' }),
    createPerk: (body: unknown) => request<{ data: unknown }>('/subscriptions/perks', { method: 'POST', body: JSON.stringify(body) }),
    updatePerk: (id: string, body: unknown) => request<{ data: unknown }>(`/subscriptions/perks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deletePerk: (id: string) => request(`/subscriptions/perks/${id}`, { method: 'DELETE' }),
    createTransaction: (body: unknown) => request<{ data: unknown }>('/subscriptions/transactions', { method: 'POST', body: JSON.stringify(body) }),
  },

  sessions: {
    tiers: () => request<{ data: unknown[] }>('/sessions/tiers').then((r) => r.data),
    slots: () => request<{ data: unknown[] }>('/sessions/slots').then((r) => r.data),
    subscriptions: () => request<{ data: unknown[] }>('/sessions/subscriptions').then((r) => r.data),
    getSubscription: (id: string) => request<{ data: unknown }>(`/sessions/subscriptions/${id}`).then((r) => r.data),
    createSubscription: (body: unknown) => request('/sessions/subscriptions', { method: 'POST', body: JSON.stringify(body) }),
    updateSubscription: (id: string, body: unknown) => request<{ data: unknown }>(`/sessions/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    createBooking: (body: unknown) => request('/sessions/bookings', { method: 'POST', body: JSON.stringify(body) }),
    updateBooking: (id: string, body: unknown) => request(`/sessions/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    allTransactions: () => request<{ data: unknown[] }>('/sessions/transactions').then((r) => r.data),
    transactions: (userId: string) => request<{ data: unknown[] }>(`/sessions/${userId}/transactions`).then((r) => r.data),
    createTier: (body: unknown) => request<{ data: unknown }>('/sessions/tiers', { method: 'POST', body: JSON.stringify(body) }),
    updateTier: (id: string, body: unknown) => request<{ data: unknown }>(`/sessions/tiers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteTier: (id: string) => request(`/sessions/tiers/${id}`, { method: 'DELETE' }),
    createSlot: (body: unknown) => request<{ data: unknown }>('/sessions/slots', { method: 'POST', body: JSON.stringify(body) }),
    updateSlot: (id: string, body: unknown) => request<{ data: unknown }>(`/sessions/slots/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteSlot: (id: string) => request(`/sessions/slots/${id}`, { method: 'DELETE' }),
    createTransaction: (body: unknown) => request<{ data: unknown }>('/sessions/transactions', { method: 'POST', body: JSON.stringify(body) }),
  },

  billing: {
    transactions: () => request<{ data: unknown[] }>('/billing/transactions').then((r) => r.data),
    invoices: () => request<{ data: unknown[] }>('/billing/invoices').then((r) => r.data),
    getInvoice: (id: string) => request<{ data: unknown }>(`/billing/invoices/${id}`).then((r) => r.data),
    createTransaction: (body: unknown) => request('/billing/transactions', { method: 'POST', body: JSON.stringify(body) }),
    createInvoice: (body: unknown) => request<{ data: unknown }>('/billing/invoices', { method: 'POST', body: JSON.stringify(body) }),
    updateInvoice: (id: string, body: unknown) => request(`/billing/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    downloadInvoicePdf: async (id: string) => {
      const res = await fetch(`${API_BASE}/billing/invoices/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to download invoice PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  volunteers: {
    records: () => request<{ data: unknown[] }>('/volunteers/records').then((r) => r.data),
    timeslots: () => request<{ data: unknown[] }>('/volunteers/timeslots').then((r) => r.data),
    getRecord: (userId: string) => request<{ data: unknown }>(`/volunteers/records/${userId}`).then((r) => r.data),
    settings: () => request<{ data: unknown }>('/volunteers/settings').then((r) => r.data),
    createTimeslot: (body: unknown) => request('/volunteers/timeslots', { method: 'POST', body: JSON.stringify(body) }),
    updateTimeslot: (id: string, body: unknown) => request(`/volunteers/timeslots/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    createAssignment: (body: unknown) => request('/volunteers/assignments', { method: 'POST', body: JSON.stringify(body) }),
    updateAssignment: (id: string, body: unknown) => request(`/volunteers/assignments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    updateSettings: (body: unknown) => request('/volunteers/settings', { method: 'PATCH', body: JSON.stringify(body) }),
    allTransactions: () => request<{ data: unknown[] }>('/volunteers/transactions').then((r) => r.data),
    transactions: (userId: string) => request<{ data: unknown[] }>(`/volunteers/${userId}/transactions`).then((r) => r.data),
    createRecord: (body: unknown) => request<{ data: unknown }>('/volunteers/records', { method: 'POST', body: JSON.stringify(body) }),
    updateRecord: (userId: string, body: unknown) => request<{ data: unknown }>(`/volunteers/records/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteTimeslot: (id: string) => request(`/volunteers/timeslots/${id}`, { method: 'DELETE' }),
    // Manual FYBIT Credit adjustment for a volunteer. `applied` differs from `requested`
    // when a deduction was clamped at a zero wallet balance.
    adjustCredits: (body: { userId: string; amount: number; description: string }) =>
      request<{ data: { applied: number; balanceAfter: number; requested: number } }>(
        '/volunteers/adjust-credits', { method: 'POST', body: JSON.stringify(body) },
      ).then((r) => r.data),
    publishFeed: () =>
      request<{ data: { jsonUrl: string; publishedAt: string; timeslotCount: number; spotsRemaining: number } }>(
        '/volunteers/publish-feed', { method: 'POST' },
      ).then((r) => r.data),
    feedStatus: () =>
      request<{ data: { publishedAt: string | null; jsonUrl: string } }>(
        '/volunteers/publish-feed/status',
      ).then((r) => r.data),
  },

  curriculum: {
    categories: () => request<{ data: unknown[] }>('/curriculum/categories').then((r) => r.data),
    courses: () => request<{ data: unknown[] }>('/curriculum/courses').then((r) => r.data),
    getCourse: (id: string) => request<{ data: unknown }>(`/curriculum/courses/${id}`).then((r) => r.data),
    badges: () => request<{ data: unknown[] }>('/curriculum/badges').then((r) => r.data),
    pathways: () => request<{ data: unknown[] }>('/curriculum/pathways').then((r) => r.data),
    enrollments: () => request<{ data: unknown[] }>('/curriculum/enrollments').then((r) => r.data),
    studentBadges: () => request<{ data: unknown[] }>('/curriculum/student-badges').then((r) => r.data),
    createCourse: (body: unknown) => request('/curriculum/courses', { method: 'POST', body: JSON.stringify(body) }),
    updateCourse: (id: string, body: unknown) => request(`/curriculum/courses/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    createEnrollment: (body: unknown) => request('/curriculum/enrollments', { method: 'POST', body: JSON.stringify(body) }),
    adminComplete: (studentId: string, courseId: string, reason: string) =>
      request<{ data: unknown; badgesAwarded: string[]; parentCourseName: string | null; classesCompleted: number }>('/curriculum/admin-complete', { method: 'POST', body: JSON.stringify({ studentId, courseId, reason }) }),
    adminRevert: (studentId: string, courseId: string, reason: string) =>
      request<{ data: unknown; classesReverted: number; parentReverted: string | null; badgesRevoked: number }>('/curriculum/admin-revert', { method: 'POST', body: JSON.stringify({ studentId, courseId, reason }) }),
    updateEnrollment: (id: string, body: unknown) => request(`/curriculum/enrollments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteEnrollment: (id: string) => request(`/curriculum/enrollments/${id}`, { method: 'DELETE' }),
    createCategory: (body: unknown) => request<{ data: unknown }>('/curriculum/categories', { method: 'POST', body: JSON.stringify(body) }),
    updateCategory: (id: string, body: unknown) => request<{ data: unknown }>(`/curriculum/categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteCategory: (id: string) => request(`/curriculum/categories/${id}`, { method: 'DELETE' }),
    createBadge: (body: unknown) => request<{ data: unknown }>('/curriculum/badges', { method: 'POST', body: JSON.stringify(body) }),
    updateBadge: (id: string, body: unknown) => request<{ data: unknown }>(`/curriculum/badges/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteBadge: (id: string) => request(`/curriculum/badges/${id}`, { method: 'DELETE' }),
    createPathway: (body: unknown) => request<{ data: unknown }>('/curriculum/pathways', { method: 'POST', body: JSON.stringify(body) }),
    updatePathway: (id: string, body: unknown) => request<{ data: unknown }>(`/curriculum/pathways/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deletePathway: (id: string) => request(`/curriculum/pathways/${id}`, { method: 'DELETE' }),
    createStudentBadge: (body: unknown) => request<{ data: unknown }>('/curriculum/student-badges', { method: 'POST', body: JSON.stringify(body) }),
    deleteStudentBadge: (id: string) => request(`/curriculum/student-badges/${id}`, { method: 'DELETE' }),
    deleteCourse: (id: string) => request(`/curriculum/courses/${id}`, { method: 'DELETE' }),
  },

  auditLog: {
    list: () => request<{ data: unknown[] }>('/audit-log').then((r) => r.data),
  },

  errorLog: {
    list: (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.page) q.set('page', String(params.page));
      if (params?.limit) q.set('limit', String(params.limit));
      if (params?.search) q.set('search', params.search);
      if (params?.status) q.set('status', params.status);
      const qs = q.toString();
      return request<{ data: unknown[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/error-log${qs ? `?${qs}` : ''}`);
    },
    clear: (all?: boolean) => request(`/error-log${all ? '?all=true' : ''}`, { method: 'DELETE' }),
  },

  preferences: {
    get: () => request<{ data: unknown }>('/preferences').then((r) => r.data),
    update: (body: unknown) => request('/preferences', { method: 'PATCH', body: JSON.stringify(body) }),
  },

  permissions: {
    list: () => request<{ data: Array<{ id: string; role: string; feature: string }>; featureKeys: string[]; roles: string[] }>('/permissions'),
    update: (role: string, features: string[]) =>
      request<{ data: Array<{ id: string; role: string; feature: string }> }>('/permissions', {
        method: 'PUT',
        body: JSON.stringify({ role, features }),
      }),
  },

  uploads: {
    profilePhoto: async (file: File, userId?: string) => {
      const formData = new FormData();
      formData.append('file', file);
      const query = userId ? `?userId=${userId}` : '';
      const res = await fetch(`${API_BASE}/uploads/profile-photo${query}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Upload failed (${res.status})`);
      }
      return res.json() as Promise<{ data: { profilePhotoUrl: string } }>;
    },
    deleteProfilePhoto: (userId?: string) => {
      const query = userId ? `?userId=${userId}` : '';
      return request(`/uploads/profile-photo${query}`, { method: 'DELETE' });
    },
  },

  stripe: {
    getConfig: () => request<{ publishableKey: string }>('/stripe/config'),
    createSubscription: (body: { tierId: string; planType: 'credit' | 'session' }) =>
      request<{ subscriptionId: string; clientSecret: string }>('/stripe/create-subscription', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  memberships: {
    plans: () => request<{ data: unknown[] }>('/memberships/plans').then((r) => r.data),
    list: () => request<{ data: unknown[] }>('/memberships').then((r) => r.data),
    my: () => request<{ data: unknown }>('/memberships/my').then((r) => r.data),
    userMemberships: (userId: string) => request<{ data: unknown[] }>(`/memberships/user/${userId}`).then((r) => r.data),
    createPlan: (body: unknown) => request<{ data: unknown }>('/memberships/plans', { method: 'POST', body: JSON.stringify(body) }),
    updatePlan: (id: string, body: unknown) => request<{ data: unknown }>(`/memberships/plans/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deletePlan: (id: string) => request<{ success: boolean }>(`/memberships/plans/${id}`, { method: 'DELETE' }),
    freeze: (id: string, durationDays: number) => request<{ data: unknown }>(`/memberships/${id}/freeze`, { method: 'POST', body: JSON.stringify({ durationDays }) }),
    cancel: (id: string, reason?: string) => request<{ data: unknown }>(`/memberships/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
    lapse: (id: string) => request<{ data: unknown }>(`/memberships/${id}/lapse`, { method: 'POST' }),
    reactivate: (id: string) => request<{ data: unknown }>(`/memberships/${id}/reactivate`, { method: 'POST' }),
    discardLapsedCredits: () => request<{ data: { processed: number; discarded: number } }>('/memberships/discard-lapsed-credits', { method: 'POST' }),
    checkout: (planId: string) => request<{ clientSecret: string }>('/memberships/checkout', { method: 'POST', body: JSON.stringify({ planId }) }),
    activate: (planId: string) => request<{ data: { membership: unknown; walletBalance: number; deducted: number } }>('/memberships/activate', { method: 'POST', body: JSON.stringify({ planId }) }),
    adminActivate: (userId: string, planId: string, reason: string) => request<{ data: { membership: unknown; walletBalance: number; deducted: number } }>('/memberships/admin-activate', { method: 'POST', body: JSON.stringify({ userId, planId, reason }) }),
    adminCheckout: (userId: string, planId: string, reason: string) => request<{ clientSecret: string }>('/memberships/admin-checkout', { method: 'POST', body: JSON.stringify({ userId, planId, reason }) }),
    // Grant a school/grant-paid membership to a set of users for a fixed window.
    // No credits are deducted and no Stripe subscription is created.
    sponsorGroup: (body: {
      userIds: string[];
      planId: string;
      startDate: string;
      endDate: string;
      sponsorName?: string | null;
      reason: string;
    }) => request<{ data: import('../types/membership').SponsorGroupResult }>('/memberships/sponsor-group', {
      method: 'POST', body: JSON.stringify(body),
    }).then((r) => r.data),
    // Replace the card/bank on the caller's card-billed membership (setup checkout).
    updatePaymentMethod: () => request<{ clientSecret: string }>('/memberships/payment-method/update', { method: 'POST' }),
    // Move the caller's credit-billed membership onto card/bank billing (charge at next cycle).
    switchToCard: (id: string) => request<{ clientSecret: string }>(`/memberships/${id}/switch-to-card`, { method: 'POST' }),
    // POS/staff variants (target a member, require a reason).
    adminUpdatePaymentMethod: (userId: string, membershipId: string, reason: string) => request<{ clientSecret: string }>('/memberships/admin-payment-method/update', { method: 'POST', body: JSON.stringify({ userId, membershipId, reason }) }),
    adminSwitchToCard: (userId: string, membershipId: string, reason: string) => request<{ clientSecret: string }>(`/memberships/admin/${membershipId}/switch-to-card`, { method: 'POST', body: JSON.stringify({ userId, reason }) }),
  },

  creditPacks: {
    catalog: () => request<{ data: unknown[] }>('/credit-packs/catalog').then((r) => r.data),
    createPack: (body: unknown) => request<{ data: unknown }>('/credit-packs/catalog', { method: 'POST', body: JSON.stringify(body) }),
    updatePack: (id: string, body: unknown) => request<{ data: unknown }>(`/credit-packs/catalog/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deletePack: (id: string) => request<{ success: boolean }>(`/credit-packs/catalog/${id}`, { method: 'DELETE' }),
    userPacks: () => request<{ data: unknown[] }>('/credit-packs/user').then((r) => r.data),
    checkout: (packId: string) => request<{ clientSecret: string }>('/credit-packs/checkout', { method: 'POST', body: JSON.stringify({ packId }) }),
    // Self-service cancel takes no reason; cancelling on a member's behalf
    // (User Profile / POS) requires one — the server rejects it otherwise.
    cancelPack: (id: string, reason?: string) =>
      request<{ data: unknown }>(`/credit-packs/user/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),
    // POS/staff variants (target a member, require a reason). `saveCard` only
    // attaches the card to the member's Stripe customer with their consent —
    // monthly packs always attach, since a subscription needs a customer.
    adminCheckout: (userId: string, packId: string, reason: string, saveCard: boolean) =>
      request<{ clientSecret: string }>('/credit-packs/admin-checkout', { method: 'POST', body: JSON.stringify({ userId, packId, reason, saveCard }) }),
    adminTopup: (userId: string, credits: number, reason: string, saveCard: boolean) =>
      request<{ clientSecret: string }>('/credit-packs/admin-topup', { method: 'POST', body: JSON.stringify({ userId, credits, reason, saveCard }) }),
  },

  addons: {
    catalog: () => request<{ data: unknown[] }>('/addons/catalog').then((r) => r.data),
    createPlan: (body: unknown) => request<{ data: unknown }>('/addons/catalog', { method: 'POST', body: JSON.stringify(body) }),
    updatePlan: (id: string, body: unknown) => request<{ data: unknown }>(`/addons/catalog/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deletePlan: (id: string) => request<{ success: boolean }>(`/addons/catalog/${id}`, { method: 'DELETE' }),
    userAddons: () => request<{ data: unknown[] }>('/addons/user').then((r) => r.data),
    userAddonsFor: (userId: string) => request<{ data: unknown[] }>(`/addons/user/${userId}`).then((r) => r.data),
    checkout: (addonId: string) => request<{ clientSecret: string }>('/addons/checkout', { method: 'POST', body: JSON.stringify({ addonId }) }),
    activate: (addonId: string) => request<{ data: { userAddon: unknown; walletBalance: number; deducted: number } }>('/addons/activate', { method: 'POST', body: JSON.stringify({ addonId }) }),
    cancelAddon: (id: string) => request<{ data: unknown }>(`/addons/user/${id}/cancel`, { method: 'POST' }),
    redemptions: () => request<{ data: unknown[] }>('/addons/redemptions').then((r) => r.data),
    userRedemptions: (userId: string) => request<{ data: unknown[] }>(`/addons/redemptions/user/${userId}`).then((r) => r.data),
    redeem: (userAddonId: string, notes?: string) => request<{ data: unknown }>('/addons/redemptions', { method: 'POST', body: JSON.stringify({ userAddonId, notes }) }),
    rollbackRedemption: (id: string, reason?: string) => request<{ success: boolean }>(`/addons/redemptions/${id}`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
  },

  wallets: {
    my: () => request<{ data: unknown }>('/wallets/my').then((r) => r.data),
    list: () => request<{ data: unknown[] }>('/wallets').then((r) => r.data),
    get: (userId: string) => request<{ data: unknown }>(`/wallets/${userId}`).then((r) => r.data),
    myTransactions: () => request<{ data: unknown[] }>('/wallets/my/transactions').then((r) => r.data),
    allTransactions: () => request<{ data: unknown[] }>('/wallets/transactions/all').then((r) => r.data),
    adjust: (body: { userId: string; amount: number; description: string }) =>
      request<{ data: unknown }>('/wallets/adjust', { method: 'POST', body: JSON.stringify(body) }),
    searchUsers: (q: string) => request<{ data: unknown[] }>(`/wallets/search-users?q=${encodeURIComponent(q)}`).then((r) => r.data),
    members: () => request<{ data: unknown[] }>('/wallets/members').then((r) => r.data),
    userTransactions: (userId: string) => request<{ data: unknown[] }>(`/wallets/user-transactions/${userId}`).then((r) => r.data),
  },

  sessionEventTemplates: {
    list: () => request<{ data: unknown[] }>('/session-event-templates').then((r) => r.data),
    create: (body: unknown) => request<{ data: unknown }>('/session-event-templates', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: unknown) => request<{ data: unknown }>(`/session-event-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<{ message: string }>(`/session-event-templates/${id}`, { method: 'DELETE' }),
  },

  sessionCategories: {
    list: () => request<{ data: unknown[] }>('/session-categories').then((r) => r.data),
    create: (body: unknown) => request<{ data: unknown }>('/session-categories', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: unknown) => request<{ data: unknown }>(`/session-categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) =>
      request<{ message: string; reassigned: { events: number; templates: number } }>(
        `/session-categories/${id}`,
        { method: 'DELETE' },
      ),
  },

  sessionEvents: {
    list: () => request<{ data: unknown[] }>('/session-events').then((r) => r.data),
    create: (body: unknown) => request<{ data: unknown }>('/session-events', { method: 'POST', body: JSON.stringify(body) }),
    createBulk: (events: unknown[]) => request<{ data: unknown[]; count: number }>('/session-events/bulk', { method: 'POST', body: JSON.stringify({ events }) }),
    update: (id: string, body: unknown) => request<{ data: unknown }>(`/session-events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<{ message: string }>(`/session-events/${id}`, { method: 'DELETE' }),
    listTrash: () => request<{ data: unknown[] }>('/session-events/trash').then((r) => r.data),
    restore: (id: string) => request<{ data: unknown }>(`/session-events/${id}/restore`, { method: 'POST' }),
    hideFromRegistrations: (id: string) => request<{ data: unknown }>(`/session-events/${id}/hide-registrations`, { method: 'POST' }),
    unhideFromRegistrations: (id: string) => request<{ data: unknown }>(`/session-events/${id}/unhide-registrations`, { method: 'POST' }),
    registrations: (eventId?: string) =>
      request<{ data: unknown[] }>(`/session-events/registrations${eventId ? `?eventId=${eventId}` : ''}`).then((r) => r.data),
    register: (eventId: string) =>
      request<{ data: unknown }>('/session-events/register', { method: 'POST', body: JSON.stringify({ eventId }) }),
    adminRegister: (userId: string, eventId: string, reason: string) =>
      request<{ data: { registration: unknown; walletBalance: number; deducted: number } }>(
        '/session-events/admin-register',
        { method: 'POST', body: JSON.stringify({ userId, eventId, reason }) },
      ),
    cancelRegistration: (regId: string) =>
      request<{ data: unknown }>(`/session-events/registrations/${regId}/cancel`, { method: 'POST' }),
    dismissRegistration: (regId: string) =>
      request<{ data: unknown }>(`/session-events/registrations/${regId}/dismiss`, { method: 'POST' }),
    adminDismissRegistration: (regId: string) =>
      request<{ data: unknown }>(`/session-events/registrations/${regId}/admin-dismiss`, { method: 'POST' }),
    updateRegistration: (regId: string, body: { status: string; cancelReason?: string }) =>
      request<{ data: unknown }>(`/session-events/registrations/${regId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    publishPublicCalendar: () =>
      request<{ data: { jsonUrl: string; icsUrl: string; publishedAt: string; eventCount: number } }>(
        '/session-events/publish-public-calendar',
        { method: 'POST' },
      ).then((r) => r.data),
    publicCalendarStatus: () =>
      request<{ data: {
        publishedAt: string | null;
        jsonUrl: string | null;
        icsUrl: string | null;
        /** Set when publishing isn't configured for this environment. */
        unavailable?: string;
      } }>(
        '/session-events/publish-public-calendar/status',
      ).then((r) => r.data),
  },

  bookings: {
    // ── Admin config ──
    getSettings: () => request<{ data: unknown }>('/bookings/settings').then((r) => r.data),
    updateSettings: (body: unknown) => request<{ data: unknown }>('/bookings/settings', { method: 'PATCH', body: JSON.stringify(body) }),
    rooms: () => request<{ data: unknown[] }>('/bookings/rooms').then((r) => r.data),
    createRoom: (body: unknown) => request<{ data: unknown }>('/bookings/rooms', { method: 'POST', body: JSON.stringify(body) }),
    updateRoom: (id: string, body: unknown) => request<{ data: unknown }>(`/bookings/rooms/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteRoom: (id: string) => request(`/bookings/rooms/${id}`, { method: 'DELETE' }),
    roomBlackouts: (roomId?: string) => request<{ data: unknown[] }>(`/bookings/room-blackouts${roomId ? `?roomId=${roomId}` : ''}`).then((r) => r.data),
    createRoomBlackout: (body: unknown, roomId?: string) =>
      request<{ data: unknown }>(`/bookings/room-blackouts${roomId ? `?roomId=${roomId}` : ''}`, { method: 'POST', body: JSON.stringify(body) }),
    deleteRoomBlackout: (id: string) => request(`/bookings/room-blackouts/${id}`, { method: 'DELETE' }),

    // ── Instructor self-service. `instructorId` is honoured for admins only —
    // everyone else is pinned to themselves server-side. ──
    myConfig: (instructorId?: string) =>
      request<{ data: unknown }>(`/bookings/me${instructorId ? `?instructorId=${instructorId}` : ''}`).then((r) => r.data),
    updateAvailability: (body: unknown, instructorId?: string) =>
      request<{ data: unknown }>(`/bookings/me/availability${instructorId ? `?instructorId=${instructorId}` : ''}`, { method: 'PUT', body: JSON.stringify(body) }),
    updateInstructorSettings: (body: unknown, instructorId?: string) =>
      request<{ data: unknown }>(`/bookings/me/settings${instructorId ? `?instructorId=${instructorId}` : ''}`, { method: 'PATCH', body: JSON.stringify(body) }),
    createBlackout: (body: unknown, instructorId?: string) =>
      request<{ data: unknown }>(`/bookings/me/blackouts${instructorId ? `?instructorId=${instructorId}` : ''}`, { method: 'POST', body: JSON.stringify(body) }),
    deleteBlackout: (id: string) => request(`/bookings/me/blackouts/${id}`, { method: 'DELETE' }),

    // ── Approvals ──
    instructors: () => request<{ data: unknown[] }>('/bookings/instructors').then((r) => r.data),
    grantApproval: (body: { instructorId: string; courseId: string }) =>
      request<{ data: unknown }>('/bookings/approvals', { method: 'POST', body: JSON.stringify(body) }),
    revokeApproval: (id: string) => request(`/bookings/approvals/${id}`, { method: 'DELETE' }),

    // ── Member-facing ──
    // Date-first: every bookable class keyed by date and time, so the member can
    // pick when they're free before picking what to work on.
    calendar: (partySize = 1) =>
      request<{ data: unknown }>(`/bookings/calendar?partySize=${partySize}`).then((r) => r.data),
    availability: (params: { classId: string; mode: string; partySize: number }) =>
      request<{ data: unknown }>(
        `/bookings/availability?classId=${encodeURIComponent(params.classId)}&mode=${params.mode}&partySize=${params.partySize}`,
      ).then((r) => r.data),
    list: () => request<{ data: unknown[] }>('/bookings').then((r) => r.data),
    create: (body: unknown) => request<{ data: unknown }>('/bookings', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    /** Is this exact username/email an active member? Used by the party roster. */
    attendeeLookup: (q: string) =>
      request<{ data: { found: boolean; firstName: string | null; username: string | null; isActiveMembership: boolean } }>(
        `/bookings/attendee-lookup?q=${encodeURIComponent(q)}`,
      ).then((r) => r.data),
    /**
     * Buy exactly the credits this booking is short by. Takes what's being
     * booked, never an amount — the server prices it and charges the difference.
     */
    creditTopup: (body: { classId: string; instructorId: string; mode: string; startTime: string; partySize: number }) =>
      request<{ clientSecret: string; credits: number }>('/bookings/credit-topup', { method: 'POST', body: JSON.stringify(body) }),
    // ── Staff-entered bookings ──
    manualOptions: () => request<{ data: unknown }>('/bookings/manual/options').then((r) => r.data),
    manualStudentSearch: (q: string) =>
      request<{ data: unknown[] }>(`/bookings/manual/students?q=${encodeURIComponent(q)}`).then((r) => r.data),
    /** Throws on 409 with `warnings` attached when staff must confirm first. */
    createManual: (body: unknown) =>
      request<{ data: unknown }>('/bookings/manual', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    cancel: (id: string, reason?: string) =>
      request<{ data: unknown }>(`/bookings/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason: reason ?? '' }) }),
    conflicts: () => request<{ data: unknown[] }>('/bookings/conflicts').then((r) => r.data),
    /** Staff-only contact cards for the booker and any named attendee with an account. */
    contacts: (id: string) => request<{ data: unknown[] }>(`/bookings/${id}/contacts`).then((r) => r.data),

    // ── Staff trash. Only cancelled or finished bookings are eligible; the
    // server skips anything else in a batch rather than failing it. ──
    listTrash: () => request<{ data: unknown[] }>('/bookings/trash').then((r) => r.data),
    trash: (ids: string[]) =>
      request<{ data: { trashed: number; skipped: number } }>(
        '/bookings/trash', { method: 'POST', body: JSON.stringify({ ids }) },
      ).then((r) => r.data),
    restore: (id: string) => request(`/bookings/${id}/restore`, { method: 'POST' }),
    /** Hard delete. Only works on a booking already in the trash. */
    permanentlyDelete: (id: string) => request(`/bookings/${id}`, { method: 'DELETE' }),
  },

  notifications: {
    list: (params?: { status?: string; unresolved?: boolean; page?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.unresolved) qs.set('unresolved', 'true');
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<{ data: unknown[] }>(`/notifications${query ? `?${query}` : ''}`).then((r) => r.data);
    },
    unreadCount: () => request<{ count: number }>('/notifications/unread-count').then((r) => r.count),
    clearAll: () =>
      request<{ data: { cleared: number } }>('/notifications/clear-all', { method: 'POST' }).then((r) => r.data),
    markRead: (id: string) => request(`/notifications/${id}`, { method: 'PATCH', body: JSON.stringify({ read: true }) }),
    update: (id: string, body: { read?: boolean; status?: string }) =>
      request(`/notifications/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    markAllRead: () => request('/notifications/mark-all-read', { method: 'POST' }),
    create: (body: { userId: string; title: string; message: string; type?: string; link?: string }) =>
      request('/notifications', { method: 'POST', body: JSON.stringify(body) }),
    delete: (id: string, reason?: string) =>
      request(`/notifications/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason ?? '' }) }),
    bulkUpdate: (ids: string[], status: 'new' | 'in_progress' | 'complete') =>
      request<{ data: { updated: number } }>('/notifications/bulk-update', {
        method: 'POST',
        body: JSON.stringify({ ids, status }),
      }),
    bulkDelete: (ids: string[], reason?: string) =>
      request<{ data: { deleted: number } }>('/notifications/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids, reason: reason ?? '' }),
      }),
  },

  media: {
    list: () => request<{ data: unknown[]; cursor: string | null }>('/media').then((r) => r.data),
    upload: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/media`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `Upload failed (${res.status})`);
      }
      return res.json() as Promise<{ data: { key: string; url: string; name: string } }>;
    },
    delete: (key: string) => request(`/media?key=${encodeURIComponent(key)}`, { method: 'DELETE' }),
  },

  webhookConfig: {
    get: () => request<{ data: { url: string; enabled: boolean; events: Record<string, boolean>; hasSecret: boolean; updatedAt: string } }>('/webhook-config').then((r) => r.data),
    update: (body: { url?: string; secret?: string | null; enabled?: boolean; events?: Record<string, boolean> }) =>
      request<{ data: { url: string; enabled: boolean; events: Record<string, boolean>; hasSecret: boolean; updatedAt: string } }>('/webhook-config', { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.data),
    test: () => request<{ success: boolean; status: number }>('/webhook-config/test', { method: 'POST' }),
  },

  emailNotifications: {
    get: () =>
      request<{ data: { enabled: boolean; rules: Record<string, { enabled: boolean; recipients: string[]; subject: string; html: string }>; updatedAt: string } }>('/email-notifications').then((r) => r.data),
    update: (body: { enabled?: boolean; rules?: Record<string, { enabled: boolean; recipients: string[]; subject: string; html: string }> }) =>
      request<{ data: { enabled: boolean; rules: Record<string, { enabled: boolean; recipients: string[]; subject: string; html: string }>; updatedAt: string } }>('/email-notifications', { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.data),
    test: (body: { recipients: string[]; subject?: string; html?: string }) =>
      request<{ success: boolean; count: number }>('/email-notifications/test', { method: 'POST', body: JSON.stringify(body) }),
  },

  shop: {
    // ── Member ──
    access: () => request<{ data: ShopAccess }>('/shop/access').then((r) => r.data),
    serviceTypes: () => request<{ data: ShopServiceType[] }>('/shop/service-types').then((r) => r.data),
    myOrders: () => request<{ data: ShopOrder[] }>('/shop/orders').then((r) => r.data),
    myOrder: (id: string) => request<{ data: ShopOrder }>(`/shop/orders/${id}`).then((r) => r.data),
    createDraft: (body: { serviceTypeId: string; options?: Record<string, unknown>; fileStatus?: ShopFileStatus }) =>
      request<{ data: ShopOrder }>('/shop/orders', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    updateDraft: (id: string, body: { options?: Record<string, unknown>; fileStatus?: ShopFileStatus }) =>
      request<{ data: ShopOrder }>(`/shop/orders/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.data),
    discardDraft: (id: string) => request(`/shop/orders/${id}`, { method: 'DELETE' }),
    /**
     * Raw-body PUT, streamed server-side. XHR rather than fetch for upload
     * progress — a 50 MB model on a school connection takes long enough that a
     * silent spinner reads as a hang.
     */
    uploadFile: (orderId: string, file: File, onProgress?: (fraction: number) => void) =>
      new Promise<ShopOrder>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${API_BASE}/shop/orders/${orderId}/files?filename=${encodeURIComponent(file.name)}`);
        xhr.withCredentials = true;
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
        xhr.onload = () => {
          let body: { data?: ShopOrder; error?: string } = {};
          try { body = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
          if (xhr.status >= 200 && xhr.status < 300 && body.data) resolve(body.data);
          else reject(new ApiError(body.error ?? `Upload failed (${xhr.status})`, xhr.status, body as Record<string, unknown>));
        };
        xhr.onerror = () => reject(new ApiError('Upload failed — check your connection and try again.', 0));
        xhr.send(file);
      }),
    addLink: (orderId: string, url: string) =>
      request<{ data: ShopOrder }>(`/shop/orders/${orderId}/links`, { method: 'POST', body: JSON.stringify({ url }) }).then((r) => r.data),
    removeFile: (orderId: string, fileId: string) =>
      request<{ data: ShopOrder }>(`/shop/orders/${orderId}/files/${fileId}`, { method: 'DELETE' }).then((r) => r.data),
    submit: (id: string) => request<{ data: ShopOrder }>(`/shop/orders/${id}/submit`, { method: 'POST' }).then((r) => r.data),
    approve: (id: string, termsAccepted: boolean) =>
      request<{ data: ShopOrder }>(`/shop/orders/${id}/approve`, { method: 'POST', body: JSON.stringify({ termsAccepted }) }).then((r) => r.data),
    cancel: (id: string) => request<{ data: ShopOrder }>(`/shop/orders/${id}/cancel`, { method: 'POST' }).then((r) => r.data),
    /** The member's own trash, for cancelled orders. Staff views are unaffected. */
    trashOrder: (id: string) => request(`/shop/orders/${id}/trash`, { method: 'POST' }),
    restoreOrder: (id: string) => request(`/shop/orders/${id}/restore`, { method: 'POST' }),
    removeOrder: (id: string) => request(`/shop/orders/${id}/remove`, { method: 'POST' }),
    /** Cookie-authenticated download link. The bucket is private; this is the only way in. */
    fileUrl: (orderId: string, fileId: string) => `${API_BASE}/shop/orders/${orderId}/files/${fileId}`,

    // ── Staff ──
    adminOrders: (params: { scope?: 'open' | 'closed' | 'all'; serviceTypeId?: string; q?: string } = {}) => {
      const q = new URLSearchParams();
      if (params.scope) q.set('scope', params.scope);
      if (params.serviceTypeId) q.set('serviceTypeId', params.serviceTypeId);
      if (params.q) q.set('q', params.q);
      return request<{ data: ShopOrder[] }>(`/shop/admin/orders?${q}`).then((r) => r.data);
    },
    adminOrder: (id: string) => request<{ data: ShopOrder }>(`/shop/admin/orders/${id}`).then((r) => r.data),
    setStatus: (id: string, to: ShopOrderStatus, reason?: string) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify({ to, reason }) }).then((r) => r.data),
    setStage: (id: string, stage: string) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/stage`, { method: 'PATCH', body: JSON.stringify({ stage }) }).then((r) => r.data),
    message: (id: string, message: string, internal: boolean) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/message`, { method: 'POST', body: JSON.stringify({ message, internal }) }).then((r) => r.data),
    quote: (id: string, body: { items: { itemId: string; credits: number; reviewFeeCredits: number }[]; note?: string; eta?: string }) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/quote`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.data),
    setFileStatus: (id: string, body: { itemId: string; fileStatus: ShopFileStatus; reason: string }) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/file-status`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.data),
    setOptions: (id: string, body: { itemId: string; options: ShopOptionValues; reason?: string }) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/options`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.data),
    refund: (id: string, body: { credits?: number; reason: string }) =>
      request<{ data: ShopOrder }>(`/shop/admin/orders/${id}/refund`, { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    adminServiceTypes: () => request<{ data: ShopServiceType[] }>('/shop/admin/service-types').then((r) => r.data),
    createServiceType: (body: Partial<ShopServiceType>) =>
      request<{ data: ShopServiceType }>('/shop/admin/service-types', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
    updateServiceType: (id: string, body: Partial<ShopServiceType>) =>
      request<{ data: ShopServiceType }>(`/shop/admin/service-types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.data),
    archiveServiceType: (id: string) => request(`/shop/admin/service-types/${id}`, { method: 'DELETE' }),
    settings: () => request<{ data: ShopSettings }>('/shop/admin/settings').then((r) => r.data),
    updateSettings: (body: Partial<ShopSettings>) =>
      request<{ data: ShopSettings }>('/shop/admin/settings', { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.data),
  },

  instructor: {
    getStudents: (params?: { search?: string; page?: number; limit?: number }) => {
      const q = new URLSearchParams();
      if (params?.search) q.set('search', params.search);
      if (params?.page) q.set('page', String(params.page));
      if (params?.limit) q.set('limit', String(params.limit));
      const qs = q.toString();
      return request<{ users: unknown[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
        `/instructor/students${qs ? `?${qs}` : ''}`,
      );
    },
  },
};
